import { useCallback } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import {
  RebookImportantInfo,
  RebookSegmentSelection,
  type RebookSummaryContentProps,
  RebookSummaryHeader,
  useRebookSummaryContent,
} from "@tenant/flights-rebook";
import {
  InfoBanner,
  InfoBannerSeverity,
  Typography,
  TypographyTypeEnum,
} from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./RebookSummaryContent.module.scss";
import { DockedButton } from "@/components/DockedButton";
import { CoreFlightsRebookComponent } from "~core-flights-rebook/components/rebook-component";
import { useFlightsRebookStyles } from "~core-flights-rebook/styles";

const COMPONENT_KEY = CoreFlightsRebookComponent.RebookSummaryContent;

export const RebookSummaryContent = (props: RebookSummaryContentProps) => {
  const { className, children, data } = props;
  const styles = useFlightsRebookStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();

  const { text, handlers } = useRebookSummaryContent(props);

  return (
    <div className={clsx(block, className)}>
      <RebookSummaryHeader data={data} />

      <InfoBanner className={cn("notice")} severity={InfoBannerSeverity.INFO}>
        <Typography
          type={TypographyTypeEnum.Heading}
          size="xs"
          weight="light"
          className={cn("notice-title")}
        >
          {t("core-flights.rebook.summary.notice.title")}
        </Typography>
        <ul className={cn("notice-list")}>
          <Trans
            i18nKey="core-flights.rebook.summary.notice.body"
            components={{
              item: (
                <Typography
                  type={TypographyTypeEnum.Paragraph}
                  size="md"
                  className={cn("notice-item")}
                  component="li"
                />
              ),
              bold: <span className={cn("bold")} />,
            }}
            values={{
              destinationName: text.destinationName,
              destinationCode: text.destinationCode,
            }}
          />
        </ul>
      </InfoBanner>

      <RebookSegmentSelection data={data}>
        <DockedButton
          primaryButtonOnClick={handlers.rebookClick}
          primaryButtonLabel={text.rebookButton}
          primaryButtonDisabled={handlers.rebookClick == null}
        />
      </RebookSegmentSelection>

      <RebookImportantInfo className={cn("important-info")} />

      {children}
    </div>
  );
};
